'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import ThemeSwitcher from '@/components/ThemeSwitcher';
// import { useTheme } from '@/lib/theme-context';
import ThemedLogo from '@/components/ThemedLogo';
import TenantSwitcher from '@/components/TenantSwitcher';

export default function Navbar() {
  const pathname = usePathname();
  const { isAuthenticated, user, logout } = useAuth();
  const role = user?.role;
  const [isOpen, setIsOpen] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  // const { theme } = useTheme();

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted) return null;

  const linkClass = (path: string) =>
    `block px-4 py-2 rounded-[var(--radius)] ${pathname === path
      ? 'bg-[var(--nav-active-bg)] text-[var(--nav-active-fg)] font-semibold'
      : 'text-[var(--nav-link)] hover:bg-[var(--nav-hover)]'
    }`;

  const roleLabel =
    role === 'OPERATOR'
      ? 'Operator'
      : role === 'ADMIN'
        ? 'Admin'
        : 'Member';

  const links = [
    // Home points at the marketing site, not the app root.
    { href: 'https://sigalmark.com', label: 'Home', external: true },
    // /certificates requires a session; offering it to anonymous visitors is a
    // dead end, and we do not let them browse issued certificates anyway.
    ...(isAuthenticated ? [{ href: '/certificates', label: 'Certificates' }] : []),
    // Verification is genuinely public — anyone with a link may check a
    // certificate, so this stays.
    { href: '/verify', label: 'Verify' },
    ...(isAuthenticated ? [{ href: '/profile', label: 'Profile' }] : []),
    ...((role === 'ADMIN' || role === 'OPERATOR') && user?.tenantId
      ? [{ href: '/tenant', label: 'Tenant' }]
      : []),
    ...(role === 'OPERATOR'
      ? [
        { href: '/admin/tenants', label: 'Tenants' },
        { href: '/admin/subscriptions', label: 'Subscriptions' },
      ]
      : []),
  ];

  return (
    <nav className="w-full bg-[var(--nav-bg)] text-[var(--nav-foreground)] px-6 py-4 border-b border-[var(--nav-border)] z-50 sticky top-0">
      <div className="max-w-[1200px] mx-auto flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <ThemedLogo className="h-14 w-auto" />
          <span
            className="font-bold text-xl tracking-wide"
          >

          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex gap-4 items-center">
          {links.map(link =>
            link.external ? (
              <a key={link.href} href={link.href} className={linkClass(link.href)}>
                {link.label}
              </a>
            ) : (
              <Link key={link.href} href={link.href} className={linkClass(link.href)}>
                {link.label}
              </Link>
            ),
          )}

          {!isAuthenticated ? (
            <Link href="/login" className={linkClass('/login')}>
              Login
            </Link>
          ) : (
            <button
              onClick={logout}
              className="px-4 py-2 text-sm text-white bg-destructive rounded-[var(--radius)] hover:bg-destructive/90"
            >
              Logout
            </button>
          )}
          <TenantSwitcher />
          <ThemeSwitcher />
        </div>

        {/* Mobile Toggle Button */}
        <button
          className="md:hidden text-[var(--nav-foreground)]"
          onClick={() => setIsOpen(prev => !prev)}
          aria-label="Toggle Menu"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Role Display */}
        {isAuthenticated && (
          <div className="hidden md:flex flex-col items-end text-sm text-[var(--nav-foreground)] opacity-80 text-right pr-4 mt-2 gap-1">
            <span>Role: {roleLabel}</span>
          </div>
        )}
      </div>

      {/* Mobile Nav Menu */}
      {isOpen && (
        <div className="md:hidden flex flex-col mt-2 border-t border-[var(--nav-border)] bg-[var(--nav-bg)] text-[var(--nav-foreground)] shadow transition-colors duration-300">
          {links.map(link =>
            link.external ? (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className={linkClass(link.href)}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className={linkClass(link.href)}
              >
                {link.label}
              </Link>
            ),
          )}

          {!isAuthenticated ? (
            <Link
              href="/login"
              onClick={() => setIsOpen(false)}
              className={linkClass('/login')}
            >
              Login
            </Link>
          ) : (
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="px-4 py-2 text-sm text-white bg-destructive rounded-[var(--radius)] hover:bg-destructive/90 m-2"
            >
              Logout
            </button>
          )}
          <div className="m-2">
            <ThemeSwitcher onThemeChange={() => setIsOpen(false)} />
          </div>
          <div className="px-4 pb-4">
            <TenantSwitcher onSwitch={() => setIsOpen(false)} />
          </div>

          {isAuthenticated && (
            <div className="block md:hidden text-sm text-[var(--nav-foreground)] opacity-80 text-right pr-4 mt-2">
              Role: {roleLabel}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
